import { V6Client } from "@aws-amplify/api-graphql";
import { FeatureFlag } from "~/api/API";
import { getFeatureFlag, listFeatureFlags } from "~/api/graphql/queries";
import { errorReporter, toError } from "~/error-handling";
import { amplifyGraphQLClient } from "~/lib/amplify-client";
import { FEATURE_FLAGS } from "./constants";
import { evaluateFeatureFlag, FeatureFlagEvaluationContext } from "./evaluate";

// Re-export for backward compatibility
export { FEATURE_FLAGS };

// Local storage key prefix for feature flag overrides
export const LOCAL_STORAGE_PREFIX = "feature-flag-override:";

/**
 * Parse and apply feature flag overrides from URL query parameters
 *
 * Supports the following formats:
 * - Single override: ?feature-flag-override=feature-name:true
 * - Multiple overrides: ?feature-flag-override=feature1:true,feature2:false
 *
 * The overrides are stored in localStorage and will persist across page loads.
 * To clear an override, set it to the opposite value or manually clear localStorage.
 */
export function applyFeatureFlagOverridesFromURL(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const overrideParam = params.get("feature-flag-override");
    const clearOverridesParam = params.get("clear-feature-flag-overrides");

    if (!overrideParam && !clearOverridesParam) {
      return;
    }

    // Clear all feature flag overrides if the param is present
    if (clearOverridesParam) {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(LOCAL_STORAGE_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
      console.log("All feature flag overrides cleared from localStorage.");
    }

    if (overrideParam) {
      // Parse comma-separated overrides
      const overrides = overrideParam.split(",");

      overrides.forEach((override) => {
        const [flagName, valueStr] = override.split(":").map((s) => s.trim());

        if (!flagName) {
          console.warn(
            `Invalid feature flag override format: "${override}". Expected format: "flag-name:true" or "flag-name:false"`,
          );
          return;
        }

        // Parse boolean value
        const value = valueStr?.toLowerCase();
        if (value !== "true" && value !== "false") {
          console.warn(
            `Invalid feature flag value for "${flagName}": "${valueStr}". Must be "true" or "false"`,
          );
          return;
        }

        const storageKey = `${LOCAL_STORAGE_PREFIX}${flagName}`;
        localStorage.setItem(storageKey, value);

        console.log(
          `Feature flag override applied: "${flagName}" = ${value} (stored in localStorage)`,
        );
      });
    }

    // Clean up URL to avoid reapplying on refresh
    const url = new URL(window.location.href);
    if (overrideParam) {
      url.searchParams.delete("feature-flag-override");
    }
    if (clearOverridesParam) {
      url.searchParams.delete("clear-feature-flag-overrides");
    }
    window.history.replaceState({}, "", url.toString());
  } catch (error) {
    console.error("Failed to apply feature flag overrides from URL:", error);
  }
}

/**
 * A resolved answer for one key. `absent` — "the table has no row for this
 * key" — is a real answer and is cached like any other; only caching `present`
 * meant every evaluation of a retired flag re-queried AppSync forever.
 */
type FlagCacheEntry =
  | { status: "present"; flag: FeatureFlag }
  | { status: "absent" };

export class FeatureFlagService {
  private static instance: FeatureFlagService;
  private client: V6Client;
  private cache: Map<string, FlagCacheEntry> = new Map();
  private cacheExpiry: Map<string, number> = new Map();
  /**
   * One in-flight request per key. Flags are read per row on the dashes, so a
   * cold cache would otherwise fire one identical request per mounted row.
   */
  private inFlight: Map<string, Promise<FeatureFlag | null>> = new Map();
  /** Per-key retry gate after a *failed* request. See `recordFailure`. */
  private failureBackoff: Map<
    string,
    { retryAt: number; consecutiveFailures: number }
  > = new Map();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  private readonly FAILURE_BACKOFF_BASE_MS = 2_000;
  private readonly FAILURE_BACKOFF_MAX_MS = 60_000;
  /** Page size for `listFeatureFlags`; one round trip at current table size. */
  private readonly LIST_PAGE_SIZE = 200;

  constructor(client: V6Client) {
    this.client = client;
  }

  static getInstance(client: V6Client): FeatureFlagService {
    if (!FeatureFlagService.instance) {
      FeatureFlagService.instance = new FeatureFlagService(client);
    }
    return FeatureFlagService.instance;
  }

  /**
   * Get a local override for a feature flag
   */
  private getLocalOverride(key: string): boolean | null {
    const storageKey = `${LOCAL_STORAGE_PREFIX}${key}`;
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored === "true") {
        return true;
      }
      if (stored === "false") {
        return false;
      }
    } catch (error) {
      console.warn(
        `Failed to get local override for feature flag "${key}":`,
        error,
      );
      // Clean up invalid data
      localStorage.removeItem(storageKey);
    }
    return null;
  }

  /**
   * Get a specific feature flag by key.
   *
   * Resolves from cache when possible, joins an in-flight request for the same
   * key when one exists, and otherwise issues exactly one request. Both
   * outcomes — a row and no row — are cached; a *failed* request is not (see
   * `fetchAndCacheFlag`).
   */
  async getFeatureFlag(key: string): Promise<FeatureFlag | null> {
    const cached = this.getCachedEntry(key);
    if (cached) {
      return cached.status === "present" ? cached.flag : null;
    }

    // Concurrent callers (one per dash row) share a single request.
    const pending = this.inFlight.get(key);
    if (pending) {
      return pending;
    }

    // A recent request for this key failed. Treat the flag as unset for now
    // rather than re-requesting on every render — during an AppSync throttling
    // event that retry pressure rises exactly as availability falls.
    const backoff = this.failureBackoff.get(key);
    if (backoff && Date.now() < backoff.retryAt) {
      return null;
    }

    const request = this.fetchAndCacheFlag(key);
    this.inFlight.set(key, request);
    return request;
  }

  private async fetchAndCacheFlag(key: string): Promise<FeatureFlag | null> {
    try {
      const response = await this.client.graphql({
        query: getFeatureFlag,
        variables: { key },
      });

      // Type assertion since we know the shape of the response
      const data = response.data as { getFeatureFlag?: FeatureFlag | null };
      const flag = data?.getFeatureFlag ?? null;

      // "No row for this key" is a definitive answer from a healthy endpoint,
      // so cache it — that is what stops the per-row re-query loop.
      this.cacheEntry(
        key,
        flag ? { status: "present", flag } : { status: "absent" },
      );
      this.failureBackoff.delete(key);
      return flag;
    } catch (error) {
      // A failed request is NOT evidence the flag is absent, so it must never
      // be cached as such — that would pin the feature off for a full cache TTL
      // on the strength of one blip. Back off instead: the next evaluation
      // returns the same "unset" default without a request until `retryAt`.
      this.recordFailure(key);
      console.error(`Failed to fetch feature flag "${key}":`, error);
      const errObj = toError(error, "Failed to fetch feature flag");
      errorReporter.reportApplicationError(errObj, {
        level: "warning",
        tags: {
          featureFlagKey: key,
          service: "FeatureFlagService",
        },
      });
      return null;
    } finally {
      this.inFlight.delete(key);
    }
  }

  /** Exponential per-key backoff, capped, reset by the next success. */
  private recordFailure(key: string): void {
    const consecutiveFailures =
      (this.failureBackoff.get(key)?.consecutiveFailures ?? 0) + 1;
    const delay = Math.min(
      this.FAILURE_BACKOFF_BASE_MS * 2 ** (consecutiveFailures - 1),
      this.FAILURE_BACKOFF_MAX_MS,
    );
    this.failureBackoff.set(key, {
      retryAt: Date.now() + delay,
      consecutiveFailures,
    });
  }

  /**
   * Get all feature flags.
   *
   * Pages through the full list. This matters beyond completeness: the seeding
   * below infers "no row" from a key's *absence* in the result, so a silently
   * truncated page would mark live flags as absent and turn their features off.
   */
  async getAllFeatureFlags(): Promise<FeatureFlag[]> {
    try {
      const flags: FeatureFlag[] = [];
      let nextToken: string | null = null;

      do {
        const response = await this.client.graphql({
          query: listFeatureFlags,
          variables: { limit: this.LIST_PAGE_SIZE, nextToken },
        });

        // Type assertion since we know the shape of the response
        const data = response.data as {
          listFeatureFlags?: {
            items?: (FeatureFlag | null)[];
            nextToken?: string | null;
          };
        };

        const items = data?.listFeatureFlags?.items || [];
        flags.push(
          ...items.filter((flag): flag is FeatureFlag => flag !== null),
        );
        nextToken = data?.listFeatureFlags?.nextToken ?? null;
      } while (nextToken);

      // Cache all flags
      flags.forEach((flag) => {
        if (flag.key) {
          this.cacheEntry(flag.key, { status: "present", flag });
        }
      });

      // The list is complete, so every known key it did not return has no row.
      // Recording those as absent up front means per-key lookups after the
      // provider's initial load resolve from cache without touching AppSync.
      this.seedAbsentForUnreturnedKnownKeys(flags);

      return flags;
    } catch (error) {
      console.error("Failed to fetch feature flags:", error);
      const errObj = toError(error, "Failed to fetch feature flags");
      errorReporter.reportApplicationError(errObj, {
        level: "warning",
        tags: {
          service: "FeatureFlagService",
        },
      });
      return [];
    }
  }

  /**
   * Evaluate if a feature is enabled for the given context
   *
   * This method first checks for local localStorage overrides before falling back to
   * server-side feature flag configuration. See README.md for how to set local overrides.
   */
  async isFeatureEnabled(
    key: string | FEATURE_FLAGS,
    context: FeatureFlagEvaluationContext = {},
  ): Promise<boolean> {
    // Check for local override first
    const localOverride = this.getLocalOverride(String(key));
    if (localOverride !== null) {
      console.log(
        `Feature flag "${String(key)}" overridden locally: ${String(localOverride)}`,
      );
      return localOverride;
    }

    const flag = await this.getFeatureFlag(key);

    if (!flag) {
      // Feature flag doesn't exist - default to disabled
      return false;
    }

    return evaluateFeatureFlag(flag, context);
  }

  /**
   * Clear the cache for a specific flag or all flags
   */
  clearCache(key?: string): void {
    if (key) {
      this.cache.delete(key);
      this.cacheExpiry.delete(key);
      this.failureBackoff.delete(key);
    } else {
      this.cache.clear();
      this.cacheExpiry.clear();
      this.failureBackoff.clear();
    }
  }

  /**
   * Record every known global key the completed list did not return as absent.
   * Skips keys already resolved, so a fresher per-key result is never clobbered
   * by an older list response finishing late.
   */
  private seedAbsentForUnreturnedKnownKeys(returnedFlags: FeatureFlag[]): void {
    const returned = new Set(
      returnedFlags.map((flag) => flag.key).filter(Boolean),
    );
    for (const key of Object.values(FEATURE_FLAGS)) {
      if (!returned.has(key) && !this.getCachedEntry(key)) {
        this.cacheEntry(key, { status: "absent" });
      }
    }
  }

  private getCachedEntry(key: string): FlagCacheEntry | null {
    const expiry = this.cacheExpiry.get(key);
    if (!expiry || Date.now() > expiry) {
      this.cache.delete(key);
      this.cacheExpiry.delete(key);
      return null;
    }
    return this.cache.get(key) ?? null;
  }

  private cacheEntry(key: string, entry: FlagCacheEntry): void {
    this.cache.set(key, entry);
    this.cacheExpiry.set(key, Date.now() + this.CACHE_TTL);
  }
}

// Singleton instance
export const featureFlagService =
  FeatureFlagService.getInstance(amplifyGraphQLClient);

// Utility functions for common usage patterns
export async function isFeatureEnabled(
  key: string | FEATURE_FLAGS,
  context: FeatureFlagEvaluationContext = {},
): Promise<boolean> {
  return featureFlagService.isFeatureEnabled(key, context);
}
